import i18n from "@/i18n";

import { intlTag } from "./languages";

/** Date/time/number formatting that follows the UI language.
 *
 *  Bare ``toLocaleString()`` formats in the BROWSER locale, so a Swedish
 *  UI showed "8/28/2026, 9:15 AM" beside Swedish labels whenever the
 *  browser was English. Every call site goes through here, or passes
 *  ``intlLocale()`` to Intl itself; never ``i18n.language``
 *  (intlArgs.test.ts). */

function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  if (navigator.languages && navigator.languages.length > 0) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

let cachedKey = "";
let cachedTag = "";

/** The Intl locale for the UI language: the reader's regional browser
 *  tag when it is the same language (en-GB keeps dd/mm/yyyy), else the
 *  registry default, always with Latin digits and the Gregorian
 *  calendar. See ``intlTag`` in languages.ts. */
export function intlLocale(
  code: string = i18n.language || "en",
  navigatorLanguages: readonly string[] = browserLanguages(),
): string {
  const key = `${code}|${navigatorLanguages.join(",")}`;
  if (key !== cachedKey) {
    cachedTag = intlTag(code, navigatorLanguages);
    cachedKey = key;
  }
  return cachedTag;
}

export function formatDateTime(value: string | number | Date): string {
  return new Date(value).toLocaleString(intlLocale());
}

export function formatDate(value: string | number | Date): string {
  return new Date(value).toLocaleDateString(intlLocale());
}

export function formatTime(value: string | number | Date): string {
  return new Date(value).toLocaleTimeString(intlLocale());
}

export function formatNumber(value: number): string {
  return value.toLocaleString(intlLocale());
}

/** A decimal with exactly ``digits`` fraction digits in the UI
 *  language ("3.1" / "3,1"): ``toFixed`` always prints a full stop,
 *  which reads wrong inside Swedish or Finnish text ("3.1 MB"). */
export function formatDecimal(value: number, digits: number): string {
  return value.toLocaleString(intlLocale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** A large count in the UI language's short form: "12.3K", "12,3 t.",
 *  "1,2 milj.". For tiles where the full figure would not fit. */
export function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat(intlLocale(), {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/** A calendar day the server sent as ``YYYY-MM-DD`` (a UTC day), in the
 *  UI language. Formatted in UTC: a bare ``new Date("2026-03-18")`` is
 *  midnight UTC, which a browser west of Greenwich would print as the
 *  17th. ``options`` picks the parts (a month name, a weekday…). */
export function formatCalendarDay(
  day: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(intlLocale(), {
    ...options,
    timeZone: "UTC",
  });
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

const RELATIVE = new Map<string, Intl.RelativeTimeFormat>();

/** "3 days ago" / "för 3 dagar sedan" / "in 2 minutes", in the UI
 *  language.
 *
 *  The unit is picked the way date-fns ``formatDistanceToNowStrict``
 *  picked it (under a minute seconds, under an hour minutes, under a day
 *  hours, under 30 days days, under 12 months months, else years, each
 *  rounded; 12 rounded months read as a year), so every "… ago" in the
 *  app reads as before. Intl.RelativeTimeFormat covers every registry
 *  language, including those date-fns has no locale for (fil, sw).
 *  ``locale`` is for memoised callers that track the language. */
export function formatRelativeTime(
  value: string | number | Date,
  now: number = Date.now(),
  locale: string = intlLocale(),
): string {
  const then = new Date(value).getTime();
  const diff = Math.abs(now - then);
  let amount: number;
  let unit: Intl.RelativeTimeFormatUnit;
  if (diff < MINUTE) {
    amount = Math.round(diff / SECOND);
    unit = "second";
  } else if (diff < HOUR) {
    amount = Math.round(diff / MINUTE);
    unit = "minute";
  } else if (diff < DAY) {
    amount = Math.round(diff / HOUR);
    unit = "hour";
  } else if (diff < MONTH) {
    amount = Math.round(diff / DAY);
    unit = "day";
  } else if (diff < YEAR) {
    amount = Math.round(diff / MONTH);
    unit = "month";
    if (amount === 12) {
      amount = 1;
      unit = "year";
    }
  } else {
    amount = Math.round(diff / YEAR);
    unit = "year";
  }
  let rtf = RELATIVE.get(locale);
  if (!rtf) {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always" });
    RELATIVE.set(locale, rtf);
  }
  // Past (and "now") is negative: -0 reads "0 seconds ago", as before.
  return rtf.format(then > now ? amount : -amount, unit);
}

/** ``formatRelativeTime``, but safe for a ``now`` sourced from a shared
 *  tick (``useNow``) that can lag the real clock by up to its interval:
 *  a row mounted right after the last tick has ``value`` (the event's
 *  own timestamp, read fresh) newer than that stale ``now``, which
 *  ``formatRelativeTime`` would read as the future ("in 12 seconds").
 *  Clamping ``now`` up to ``Date.now()`` here — never down — keeps every
 *  other caller of ``formatRelativeTime`` (passing a fresh ``now`` or
 *  none at all) unaffected. */
export function relativeAge(
  value: string | number | Date,
  now: number,
  locale: string = intlLocale(),
): string {
  return formatRelativeTime(value, Math.max(now, Date.now()), locale);
}
